<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { Splide, SplideSlide } from '@splidejs/vue-splide';
    import type { Options as SplideOptions, Splide as SplideInstance } from '@splidejs/splide';
    import type { OnsiteNavigationStoryblok } from '~storyblok-types/OnsiteNavigationStoryblok';
    import type { OnsiteNavigationItemStoryblok } from '~storyblok-types/OnsiteNavigationItemStoryblok';
    import '@splidejs/vue-splide/css';

    type OnsiteNavigationItem = {
        uid: string;
        label: string;
        anchor: string;
        href: string;
    };

    type ValidOnsiteNavigationItemStoryblok = OnsiteNavigationItemStoryblok & {
        Anchor: string;
        Label: string;
    };

    const props = defineProps<{
        blok: OnsiteNavigationStoryblok;
    }>();

    const activeAnchor = ref<string | null>(null);
    const targetAnchor = ref<string | null>(null);
    const isProgrammaticScroll = ref(false);
    const splideRef = ref<InstanceType<typeof Splide> | null>(null);

    const onsiteNavigationRef = useTemplateRef<HTMLElement>('onsiteNavigationRef');
    const onsiteNavigationInnerRef = useTemplateRef<HTMLElement>('onsiteNavigationInnerRef');
    const isSticky = ref(false);
    const headerStickyOffset = ref(0);

    let sectionObserver: IntersectionObserver | null = null;
    let headerClassObserver: MutationObserver | null = null;
    let programmaticScrollTimeout: number | null = null;
    let scrollToActiveSlideTimeout: number | null = null;
    let lastCenteredIndex: number | null = null;
    let stickyUpdateFrame: number | null = null;

    const items = computed<OnsiteNavigationItem[]>(() => {
        const links: OnsiteNavigationItemStoryblok[] = props.blok.Links ?? [];

        return links
            .filter((item): item is ValidOnsiteNavigationItemStoryblok => {
                return Boolean(item.Anchor && item.Label);
            })
            .map((item) => {
                const anchor = item.Anchor.replace(/^#/, '');

                return {
                    uid: item._uid,
                    label: item.Label,
                    anchor,
                    href: `#${anchor}`
                };
            });
    });

    const splideOptions: SplideOptions = {
        type: 'slide',
        autoWidth: true,
        focus: 0,
        start: 0,
        rewind: false,
        pagination: false,
        arrows: true,
        drag: true,
        snap: false,
        gap: '0',
        trimSpace: true,
        omitEnd: true,
        perMove: 1
    };

    const isMobileViewport = () => {
        return window.matchMedia('(max-width: 767px)').matches;
    };

    const stopProgrammaticScrollLock = () => {
        isProgrammaticScroll.value = false;
        targetAnchor.value = null;

        if (programmaticScrollTimeout) {
            window.clearTimeout(programmaticScrollTimeout);
            programmaticScrollTimeout = null;
        }
    };

    const startProgrammaticScrollLock = (anchor: string) => {
        isProgrammaticScroll.value = true;
        targetAnchor.value = anchor;

        if (programmaticScrollTimeout) {
            window.clearTimeout(programmaticScrollTimeout);
        }

        programmaticScrollTimeout = window.setTimeout(() => {
            stopProgrammaticScrollLock();
        }, 900);
    };

    const getStickyTopOffset = () => {
        const onsiteNavigation = onsiteNavigationRef.value;

        if (!onsiteNavigation) {
            return 0;
        }

        const top = window.getComputedStyle(onsiteNavigation).top;
        const parsedTop = Number.parseFloat(top);

        return Number.isNaN(parsedTop) ? 0 : parsedTop;
    };

    const getStickyVisualOffset = () => {
        const onsiteNavigation = onsiteNavigationRef.value;
        const onsiteNavigationInner = onsiteNavigationInnerRef.value;

        if (!onsiteNavigation || !onsiteNavigationInner) {
            return 0;
        }

        if (!onsiteNavigation.previousElementSibling?.classList.contains('hero-container')) {
            return 0;
        }

        return onsiteNavigationInner.getBoundingClientRect().height / 2;
    };

    const updateStickyState = () => {
        if (stickyUpdateFrame !== null) {
            return;
        }

        stickyUpdateFrame = window.requestAnimationFrame(() => {
            stickyUpdateFrame = null;
            updateHeaderStickyOffset();

            const onsiteNavigation = onsiteNavigationRef.value;

            if (!onsiteNavigation) {
                return;
            }

            isSticky.value =
                onsiteNavigation.getBoundingClientRect().top <= getStickyTopOffset() + getStickyVisualOffset() + 1;
        });
    };

    const updateHeaderStickyOffset = (forceRebuild = false) => {
        const header = document.querySelector<HTMLElement>('header.header');
        const headerCore = header?.querySelector<HTMLElement>('.header-core');
        const nextOffset = headerCore?.classList.contains('--sticky-visible')
            ? headerCore.getBoundingClientRect().bottom
            : 0;

        if (!forceRebuild && nextOffset === headerStickyOffset.value) {
            return;
        }

        headerStickyOffset.value = nextOffset;

        nextTick(() => {
            updateStickyState();
        });
    };

    const onResize = () => {
        updateHeaderStickyOffset(true);
    };

    const scrollToActiveSlide = () => {
        const activeIndex = items.value.findIndex((item) => item.anchor === activeAnchor.value);

        if (activeIndex === -1) {
            return;
        }

        const splide = splideRef.value?.splide as SplideInstance | undefined;

        if (!splide) {
            return;
        }

        if (lastCenteredIndex === activeIndex) {
            return;
        }

        lastCenteredIndex = activeIndex;

        if (!isMobileViewport()) {
            splide.go(activeIndex);
            return;
        }

        nextTick(() => {
            const slide = splide.Components.Slides.getAt(activeIndex)?.slide;
            const track = splide.root.querySelector<HTMLElement>('.splide__track');
            const list = splide.root.querySelector<HTMLElement>('.splide__list');

            if (!slide || !track || !list) {
                return;
            }

            const trackWidth = track.clientWidth;
            const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
            const targetPosition = slideCenter - trackWidth / 2;

            const maxPosition = list.scrollWidth - trackWidth;
            const clampedPosition = Math.max(0, Math.min(targetPosition, maxPosition));

            splide.Components.Move.translate(-clampedPosition);
        });
    };

    const onClick = (anchor: string) => {
        activeAnchor.value = anchor;
        startProgrammaticScrollLock(anchor);

        nextTick(() => {
            lastCenteredIndex = null;
            scrollToActiveSlide();
        });
    };

    const onSplideMounted = () => {
        if (!activeAnchor.value && items.value[0]) {
            activeAnchor.value = items.value[0].anchor;
        }

        nextTick(() => {
            const splide = splideRef.value?.splide as SplideInstance | undefined;

            splide?.refresh();
            lastCenteredIndex = null;
            scrollToActiveSlide();
        });
    };

    onMounted(() => {
        const sectionElements = items.value
            .map((item) => document.getElementById(item.anchor))
            .filter((element): element is HTMLElement => Boolean(element));

        sectionObserver = new IntersectionObserver(
            (entries) => {
                const visibleEntries = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

                const activeEntry = visibleEntries[0];

                if (!activeEntry?.target?.id) {
                    return;
                }

                const anchor = activeEntry.target.id;

                if (isProgrammaticScroll.value) {
                    if (anchor === targetAnchor.value) {
                        stopProgrammaticScrollLock();
                    }

                    return;
                }

                activeAnchor.value = anchor;
            },
            {
                root: null,
                rootMargin: '-35% 0px -55% 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1]
            }
        );

        sectionElements.forEach((element) => {
            sectionObserver?.observe(element);
        });

        if (!activeAnchor.value && items.value[0]) {
            activeAnchor.value = items.value[0].anchor;
        }

        nextTick(() => {
            updateHeaderStickyOffset();

            const header = document.querySelector<HTMLElement>('header.header');
            const headerCore = header?.querySelector<HTMLElement>('.header-core');

            if (!headerCore) {
                return;
            }

            headerClassObserver = new MutationObserver(() => {
                updateHeaderStickyOffset();
            });

            headerClassObserver.observe(headerCore, {
                attributes: true,
                attributeFilter: ['class']
            });
        });

        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', updateStickyState, { passive: true });
    });

    watch(activeAnchor, () => {
        if (scrollToActiveSlideTimeout) {
            window.clearTimeout(scrollToActiveSlideTimeout);
        }

        scrollToActiveSlideTimeout = window.setTimeout(
            () => {
                scrollToActiveSlide();
            },
            isMobileViewport() ? 160 : 0
        );
    });

    onBeforeUnmount(() => {
        sectionObserver?.disconnect();
        headerClassObserver?.disconnect();

        if (programmaticScrollTimeout) {
            window.clearTimeout(programmaticScrollTimeout);
        }

        if (scrollToActiveSlideTimeout) {
            window.clearTimeout(scrollToActiveSlideTimeout);
        }

        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', updateStickyState);

        if (stickyUpdateFrame !== null) {
            window.cancelAnimationFrame(stickyUpdateFrame);
        }
    });
</script>

<template>
    <nav
        v-if="items.length"
        ref="onsiteNavigationRef"
        v-editable="blok"
        class="onsite-navigation"
        :style="{
            '--onsite-navigation-header-offset': `${headerStickyOffset}px`
        }"
        :class="{
            'onsite-navigation--is-sticky': isSticky
        }"
        aria-label="Seitennavigation"
    >
        <div
            ref="onsiteNavigationInnerRef"
            class="onsite-navigation__inner"
        >
            <Splide
                ref="splideRef"
                class="onsite-navigation__slider"
                :options="splideOptions"
                :has-track="true"
                @splide:mounted="onSplideMounted"
            >
                <SplideSlide
                    v-for="item in items"
                    :key="item.uid"
                    class="onsite-navigation__item"
                >
                    <a
                        class="onsite-navigation__link first-layer-link"
                        :class="{
                            'link-active': activeAnchor === item.anchor,
                            'onsite-navigation__link--active': activeAnchor === item.anchor
                        }"
                        :aria-current="activeAnchor === item.anchor ? 'location' : undefined"
                        :href="item.href"
                        @click="onClick(item.anchor)"
                    >
                        <span class="section-label">{{ item.label }}</span>

                        <span
                            class="arrow-down-icon"
                            aria-hidden="true"
                        />
                    </a>
                </SplideSlide>
            </Splide>
        </div>
    </nav>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/navigation/onsite-navigation.scss';
</style>
